// Camera-presence anti-cheat. Pairs with index.ts: this module owns the
// MediaStream singleton + face-detection loop; it emits AntiCheatPayloads
// through a callback that the React hook wires into the existing batch queue.
//
// Detection runs every 10 s. The state machine emits sparingly:
//   - camera_multi_face on transitions into >=2 faces (not every tick)
//   - camera_no_face once per absence, at the moment a face reappears — or,
//     still open, when the monitor stops or the page is hidden (a tab closed
//     mid-absence: pageHide() puts it on the controller's final beacon)
//
// Models: TinyFaceDetector only (~190 KB), loaded from /models-v1/face-api.
// Without WebGL, tfjs runs it on wasm from /tfjs-wasm-4.22.0 (~425 KB).

// @vladmandic/face-api is ~1.1 MB — the single biggest dependency. It's
// dynamically imported (inside loadModels() below) so it lands in its own async
// chunk instead of the candidate/main entry bundle. Only candidates reach the
// detection loop, and only after the camera-consent step; Gate.tsx warms this
// chunk on idle (scheduleIdle → loadModels) so monitor.start() awaits an
// already-resolved import. Recruiters never load it at all.
type FaceApi = typeof import("@vladmandic/face-api");
// The slice of the bundled tfjs engine loadModels() needs: exported at runtime
// (dist/face-api.esm.js, tfjs 4.22.0), but face-api's typings declare only the
// ops face-api itself calls.
interface TfBackendSetup {
  setWasmPaths(prefix: string): void;
  env(): { set(flag: string, value: boolean): void };
  ready(): Promise<void>;
}

import { captureSilentFailure } from "@/lib/observability";

import type { AntiCheatPayload, CameraDenyReason } from "./events";

// Immutable-cached by URL: when you refresh the vendored model bytes, rename the
// dir AND bump the version, or stale bytes are served forever.
const MODELS_URI = "/models-v1/face-api";
// tfjs's wasm binaries (see loadModels). Same immutable rule, and one more:
// they must match the tfjs version face-api BUNDLES (`tf.version_wasm`), so a
// face-api upgrade that moves it re-vendors under a new dir name (README.md
// inside the dir).
const TFJS_WASM_URI = "/tfjs-wasm-4.22.0/";
const DEFAULT_INTERVAL_MS = 10_000;
// 224 is the inputSize face-api.js's TinyFaceDetector documents as the
// accuracy/perf sweet spot. Lower = faster, less accurate. We're counting
// faces, not recognizing them, so 224 is plenty.
// ⚠ Never shrink it to make a slow backend faster (tfjs's cpu fallback blocks
// the main thread ~0.4–0.8 s per detect): at normal webcam distance (face ≈
// 20% of the frame width) 160 and 128 found the face in 1 of 6 samples vs 6 of
// 6 at 224 (2026-10-24, FRONTEND-REACT-A verify) — a present candidate would be
// flagged camera_no_face. The speed fix is the backend (wasm, loadModels).
const DETECTOR_INPUT_SIZE = 224;
const DETECTOR_SCORE_THRESHOLD = 0.5;
const CAPTURE_WIDTH = 320;
const CAPTURE_HEIGHT = 240;
// Number of consecutive ticks with video.readyState < HAVE_CURRENT_DATA we
// tolerate before treating the tick as a synthetic miss (count = 0). One
// silent tick covers the cold-start race (first tick fires immediately on
// start() before the off-DOM <video> has decoded its first frame). Two
// stalled ticks in a row (~20 s) means the stream genuinely never started —
// don't silently mute the detector forever.
const STALLED_TICK_GRACE = 1;
// EF-8: how long start() waits for the detection model before running WITHOUT
// it. The models are same-origin (~190 KB) and Gate.tsx warms them on idle, so
// reaching this means something is wrong (a hung fetch, a blocked chunk) —
// and the photos, which need no model at all, must not wait forever for it.
// A load that lands later is picked up by the next tick: `faceapiMod` is
// module-level and tick() re-reads it.
const MODEL_LOAD_TIMEOUT_MS = 20_000;

let activeStream: MediaStream | null = null;
let modelsLoadPromise: Promise<void> | null = null;
// Resolved @vladmandic/face-api module, cached after the first dynamic import in
// loadModels(). Set as soon as the CHUNK lands, which is why it is not the
// readiness signal — see `modelsReady`.
let faceapiMod: FaceApi | null = null;
// True only once the detector weights are actually loaded. tick() gates every
// presence claim on this: start() no longer guarantees a working detector (no
// tfjs backend that initialises, a blocked chunk, an offline first load).
let modelsReady = false;
// tfjs throws on setWasmPaths once a wasm start has ABORTED (a missing or
// blocked binary — its initAborted flag, cleared only by a later success), and
// a retried loadModels() follows exactly such a failure, so it must not repeat
// the call. Per page load, like the engine it configures.
let wasmPathsSet = false;

// CP-14 — camera-switch detection. The client never decides "switched": it
// reports the camera's device label with EVERY camera_started (gate grant,
// resume after a reload, re-acquire after the track ended) and the server
// derives the switch from the distinct labels it has seen. The state below is
// module-level because it must outlive the per-Question monitor (Question
// remounts on every Next/Back): the automatic re-acquire budget is per page
// load, so a flapping camera or a revoked permission can never become a
// getUserMedia storm or a permission re-prompt on every question.
export const CAMERA_LABEL_MAX = 120;
const MAX_AUTO_REACQUIRES = 3;
// A Firefox doorhanger left unanswered keeps getUserMedia pending forever;
// past this the monitor falls back to the plain revoke path.
const REACQUIRE_TIMEOUT_MS = 15_000;
// One in-flight getUserMedia shared by concurrent callers (StrictMode's double
// effect run, a resume racing a track-ended re-acquire) — two calls would open
// two streams and leak the first with the camera light on.
let pendingRequest: Promise<MediaStream> | null = null;
// Bumped by disposeActiveStream(); a request that resolves after the epoch it
// was started in must not install its stream (Done.tsx released the camera
// after submit — installing a late stream would leave the light on).
let disposeEpoch = 0;
let autoReacquires = 0;
let autoReacquireExhausted = false;
// The one resume attempt per page load, memoised as the ATTEMPT (not the
// stream): a later mount must re-read the singleton, never a stale stream.
let resumeAttempt: Promise<void> | null = null;

/** The label of the stream's camera device ("Integrated Webcam (04f2:b6d9)",
 * "OBS Virtual Camera"), trimmed and bounded; null when the browser reports
 * none. Device identity only — nothing from the frame. */
export function describeCameraDevice(stream: MediaStream): string | null {
  const label = stream.getVideoTracks()[0]?.label?.trim() ?? "";
  return label ? label.slice(0, CAMERA_LABEL_MAX) : null;
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const id = window.setTimeout(
      () => reject(new Error("camera re-acquire timed out")),
      ms,
    );
    promise.then(
      (value) => {
        window.clearTimeout(id);
        resolve(value);
      },
      (err) => {
        window.clearTimeout(id);
        reject(err);
      },
    );
  });
}

// Subscribers are notified whenever the singleton transitions: grant, dispose,
// lazy-null on a dead track, or revoke. Used by CameraPreview to mount/unmount
// without polling. Notify-on-transition only — re-setting the same stream
// reference does NOT notify (no-op for subscribers).
const streamSubscribers = new Set<(stream: MediaStream | null) => void>();

function notifyStreamSubscribers(): void {
  const current = activeStream;
  for (const cb of streamSubscribers) {
    try {
      cb(current);
    } catch {
      // A buggy subscriber must never break the stream lifecycle.
    }
  }
}

export function subscribeToStream(
  cb: (stream: MediaStream | null) => void,
): () => void {
  streamSubscribers.add(cb);
  return () => {
    streamSubscribers.delete(cb);
  };
}

export function loadModels(): Promise<void> {
  if (!modelsLoadPromise) {
    // Dynamically import face-api here (not at module top) so the ~1.1 MB lib
    // stays out of the candidate/main entry chunk. Cache the module for tick().
    modelsLoadPromise = import("@vladmandic/face-api")
      .then(async (mod) => {
        faceapiMod = mod;
        // FRONTEND-REACT-A: settle tfjs on a backend BEFORE the weights become
        // tensors. The bundle registers webgl and wasm (both priority 2,
        // webgl first) and cpu (1), and tf.ready() takes the first that
        // starts. It ships wasm's glue but no .wasm binary: without a path,
        // a browser without WebGL fetched the SPA's index.html as the binary
        // (and the weights, loaded un-awaited, threw "backend 'wasm' has not
        // yet been initialized"). Removing wasm instead landed those browsers
        // on cpu, whose 0.55–0.81 s detect blocked typing every tick. So:
        // point wasm at our own copy of its binaries. WebGL is unchanged —
        // still first, and it never fetches them — and if wasm cannot start
        // either, ready() still falls through to cpu. Threads stay off: they
        // need cross-origin isolation, which the app does not have, and
        // their binary is not vendored.
        const tf = mod.tf as unknown as TfBackendSetup;
        if (!wasmPathsSet) {
          tf.setWasmPaths(TFJS_WASM_URI);
          tf.env().set("WASM_HAS_MULTITHREAD_SUPPORT", false);
          wasmPathsSet = true;
        }
        await tf.ready();
        return mod.nets.tinyFaceDetector.loadFromUri(MODELS_URI);
      })
      // EF-8: readiness is the WEIGHTS, not the module. `faceapiMod` is set
      // one line above — before the weights load — so a failed/hung
      // loadFromUri leaves a usable-looking handle that can only throw. tick()
      // must read this flag, never the handle, or it calls a detector that
      // cannot detect (and, on the stalled-video path, would bank "no face"
      // stretches nobody measured).
      .then(() => {
        modelsReady = true;
      })
      .catch((err) => {
        // Reset so a later retry doesn't get stuck on a rejected promise.
        modelsLoadPromise = null;
        throw err;
      });
  }
  return modelsLoadPromise;
}

export function getActiveStream(): MediaStream | null {
  if (!activeStream) return null;
  const tracks = activeStream.getVideoTracks();
  if (tracks.length === 0 || tracks.every((t) => t.readyState === "ended")) {
    activeStream = null;
    notifyStreamSubscribers();
    return null;
  }
  return activeStream;
}

export function disposeActiveStream(): void {
  // Invalidate any getUserMedia still pending — even when nothing is
  // installed yet (the dead stream was already lazily nulled).
  disposeEpoch++;
  if (!activeStream) return;
  for (const track of activeStream.getTracks()) {
    track.stop();
  }
  activeStream = null;
  notifyStreamSubscribers();
}

export interface CameraStreamError extends Error {
  reason: CameraDenyReason;
}

function streamError(reason: CameraDenyReason, message: string): CameraStreamError {
  const err = new Error(message) as CameraStreamError;
  err.reason = reason;
  return err;
}

function mapGetUserMediaError(e: unknown): CameraStreamError {
  const name = e instanceof Error ? e.name : "";
  const message = e instanceof Error ? e.message : String(e);
  // getUserMedia error names per the MediaStream spec.
  switch (name) {
    case "NotAllowedError":
    case "SecurityError":
      return streamError("denied", message);
    case "NotFoundError":
    case "OverconstrainedError":
      return streamError("no_device", message);
    case "NotReadableError":
    case "AbortError":
      return streamError("in_use", message);
    default:
      return streamError("error", message);
  }
}

export function requestCameraStream(): Promise<MediaStream> {
  const existing = getActiveStream();
  if (existing) return Promise.resolve(existing);
  if (pendingRequest) return pendingRequest;
  if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
    return Promise.reject(streamError("error", "MediaDevices API unavailable"));
  }
  const epoch = disposeEpoch;
  const request = (async (): Promise<MediaStream> => {
    let stream: MediaStream;
    try {
      // No width/height hints: ask the camera for its native resolution and
      // let the canvas downscale to CAPTURE_WIDTH x CAPTURE_HEIGHT below.
      // Asking the *camera* for 320x240 forces a sensor-side downscale
      // (notably grainy on Mac built-in FaceTime cameras whose native
      // pipeline runs at 720p+), which hurt TinyFaceDetector confidence and
      // produced spurious no-face misses. The browser's canvas downscale of a
      // 720p source is sharper.
      stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" },
        audio: false,
      });
    } catch (e) {
      throw mapGetUserMediaError(e);
    }
    if (epoch !== disposeEpoch) {
      for (const track of stream.getTracks()) {
        track.stop();
      }
      throw streamError("error", "Camera released while the request was pending");
    }
    activeStream = stream;
    // night-p5 verify (2026-10-08): the singleton must learn that its stream
    // DIED, not only that it was disposed. Track 'ended' fires on an OS
    // camera toggle, a site-settings revoke or another app taking the
    // device, and until now only the monitor (the timed flow) listened —
    // Practice and the Gate's preview subscribe to this singleton with no
    // monitor running, so an intro that said "Camera enabled" kept saying it
    // over a preview whose track had ended. `getActiveStream` already nulls
    // a dead stream lazily and notifies; firing it here makes that eager.
    // Guarded on identity so a stream the monitor has since replaced is not
    // re-read; `track.stop()` (dispose) never fires 'ended', so a dispose is
    // not notified twice.
    for (const track of stream.getVideoTracks()) {
      track.addEventListener("ended", () => {
        if (activeStream === stream) getActiveStream();
      });
    }
    notifyStreamSubscribers();
    return stream;
  })();
  pendingRequest = request.finally(() => {
    pendingRequest = null;
  });
  return pendingRequest;
}

/**
 * CP-14 — resume the camera on a Question mount that finds no live stream.
 *
 * A MediaStream never survives a reload, so before CP-14 a mid-test reload (or
 * a crash caught by the route boundary) left the camera silently OFF for the
 * rest of the attempt — and that reload is exactly how a Chrome candidate
 * switches cameras (the omnibox picker applies on the next page load). One
 * getUserMedia per page load: concurrent mounts (StrictMode, a fast Next)
 * share the attempt, and it is a no-op once automatic re-acquisition is
 * exhausted. The outcome is reported here, once, through `emit` — the caller
 * passes the DURABLE emitter (lib/api emitEvent), never the controller's
 * queue, which the next remount disposes. Callers re-read getActiveStream()
 * afterwards; nothing is returned on purpose (see `resumeAttempt`).
 */
export function resumeCameraStream(
  emit: (payload: AntiCheatPayload) => void,
): Promise<void> {
  if (getActiveStream() || autoReacquireExhausted) return Promise.resolve();
  if (!resumeAttempt) {
    resumeAttempt = (async (): Promise<void> => {
      try {
        const stream = await requestCameraStream();
        emit({
          type: "camera_started",
          device_label: describeCameraDevice(stream),
        });
      } catch (err) {
        autoReacquireExhausted = true;
        const reason: CameraDenyReason =
          (err as CameraStreamError)?.reason ?? "error";
        emit({ type: "camera_permission_denied", reason });
      }
    })();
  }
  return resumeAttempt;
}

// EF-8 (Build B) — camera snapshots. The wire shape of the schedule the
// server froze at /start (`camera_snapshots` on the /start and /question
// payloads); `extras` are the event triggers a photo may be added for.
export type SnapshotTrigger =
  | "interval"
  | "multi_face"
  | "face_return"
  | "fullscreen_exit"
  | "tab_return";

export interface SnapshotConfig {
  interval_s: number;
  min_gap_s: number;
  extras: string[];
}

// "ok" = accepted or skipped (429), "retry" = a transient failure (one retry
// after 2 s, then dropped), "stop" = the attempt no longer takes photos
// (404 / 410, or a client bug — 413 / 415): snapshots stop for good.
export type SnapshotUploadResult = "ok" | "stop" | "retry";

export type SnapshotUploader = (
  blob: Blob,
  trigger: SnapshotTrigger,
  capturedAt: string,
  seq: number,
) => Promise<SnapshotUploadResult>;

// A 640-wide JPEG at quality 0.7 is ~40 KB (the number the legal facts sheet
// cites); aspect-preserving so a 16:9 camera is not squashed like the
// 320×240 detection canvas.
const SNAPSHOT_MAX_WIDTH = 640;
const SNAPSHOT_FALLBACK_HEIGHT = 480;
const SNAPSHOT_QUALITY = 0.7;
// At most this many photos wait for the network; beyond it the OLDEST is
// dropped — the recruiter wants recent photos, not a backlog.
const SNAPSHOT_QUEUE_CAP = 3;
// OPS-28 #14: the SAME backoff ladder as the answer queue (lib/answerQueue.ts,
// OPS-22) — ~27.6 s, enough to ride out a deploy or a Fly restart. The old
// single 2 s retry lost every photo of a 5–30 s outage, and nothing said so.
// The photo being retried stays at the head: the queue behind it keeps its cap.
const SNAPSHOT_RETRY_BACKOFF_MS = [400, 1200, 3000, 8000, 15000];

interface SnapshotJob {
  blob: Blob;
  trigger: SnapshotTrigger;
  capturedAt: string;
  seq: number;
}

// ─── EF-8: the ONE snapshot schedule per attempt ─────────────────────────────
// Photo cadence (Leo, 2026-10-10): the consent says "one every 30 seconds
// while the test runs" plus the event extras, never less than 5 s apart — so
// the schedule is owned HERE, beside the stream singleton, not by the
// per-page monitor. App.tsx keys the candidate route's crash boundary by
// pathname, so every Next/Back remounts Question.tsx and with it the monitor:
// a per-monitor timer restarted on each question and shot at once (a photo
// per question arrival), while Review and SectionReview, which mount no
// monitor, took none for as long as the clock ran there.
//
// Every page on which the test is RUNNING holds the schedule
// (`holdSnapshotSchedule`): the question page through its monitor, the
// review pages directly. The first hold of an attempt arms it and the first
// photo comes when the running test first shows; a release by the last
// holder PAUSES it keeping its place, and the next hold resumes it where it
// was — a route change takes no photo unless one was due. Paused.tsx, a
// break, the Gate and Practice hold nothing, so nothing is taken there.
// Extras (`signalSnapshot`) keep the minimum gap and never move the
// interval. The place is persisted per attempt in sessionStorage, so a
// mid-test reload does not restart the cadence either.
const SNAPSHOT_SCHEDULE_PREFIX = "anticheat:snapshots:";
// The config is read LAZILY — it arrives with the first /question payload,
// after the first hold — so a held schedule without one re-reads it this often.
const SNAPSHOT_CONFIG_POLL_MS = 1000;
// A due photo that found no decoded frame (a cold <video>, a stream mid
// re-acquire) tries again this soon, not a whole interval later.
const SNAPSHOT_NOT_READY_RETRY_MS = 1000;

export interface SnapshotScheduleOptions {
  // The attempt the schedule belongs to: a hold for another attempt starts a
  // fresh one, and this attempt's place survives a reload under this key.
  // Empty = anonymous (never persisted).
  attemptId: string;
  getConfig: () => SnapshotConfig | null;
  upload: SnapshotUploader;
}

interface SnapshotSchedule {
  key: string;
  getConfig: () => SnapshotConfig | null;
  upload: SnapshotUploader;
  holders: number;
  timerId: number | null;
  // When the next INTERVAL photo is due (epoch ms); 0 = now. Extras never
  // move it.
  nextIntervalAt: number;
  // The last photo of any trigger, for the minimum gap.
  lastShotAt: number;
  // "stop" from the uploader (404 / 410, or a client bug — 413 / 415): no
  // more photos for this attempt, whoever holds it.
  stopped: boolean;
  // The client's own counter (metadata only — the server mints the real
  // sequence number). The queue holds at most SNAPSHOT_QUEUE_CAP photos and
  // ONE upload is in flight at a time; it drains on across a route change.
  seq: number;
  queue: SnapshotJob[];
  inFlight: boolean;
  // Its own off-DOM <video> on the stream singleton — the review pages have
  // no monitor to borrow one from.
  video: HTMLVideoElement | null;
  canvas: HTMLCanvasElement | null;
  unsubscribe: () => void;
}

let snapshotSchedule: SnapshotSchedule | null = null;

function readSchedulePlace(key: string): { next: number; last: number } {
  if (!key) return { next: 0, last: 0 };
  try {
    const raw = sessionStorage.getItem(SNAPSHOT_SCHEDULE_PREFIX + key);
    if (!raw) return { next: 0, last: 0 };
    const parsed = JSON.parse(raw) as { next?: unknown; last?: unknown };
    const num = (v: unknown): number =>
      typeof v === "number" && Number.isFinite(v) && v > 0 ? v : 0;
    return { next: num(parsed.next), last: num(parsed.last) };
  } catch {
    return { next: 0, last: 0 };
  }
}

function persistSchedulePlace(s: SnapshotSchedule): void {
  if (!s.key) return;
  try {
    sessionStorage.setItem(
      SNAPSHOT_SCHEDULE_PREFIX + s.key,
      JSON.stringify({ next: s.nextIntervalAt, last: s.lastShotAt }),
    );
  } catch {
    /* storage unavailable — the place lives in this module only */
  }
}

function playQuietly(video: HTMLVideoElement): void {
  try {
    const p = video.play() as Promise<void> | undefined;
    if (p && typeof p.catch === "function") p.catch(() => {});
  } catch {
    /* no source, or an environment without media playback */
  }
}

function bindScheduleStream(s: SnapshotSchedule, stream: MediaStream | null): void {
  if (!stream) {
    if (s.video) {
      try {
        s.video.pause();
      } catch {
        /* decorative */
      }
      s.video.srcObject = null;
    }
    return;
  }
  if (!s.video) {
    const video = document.createElement("video");
    video.autoplay = true;
    video.muted = true;
    video.playsInline = true;
    s.video = video;
  }
  if (s.video.srcObject !== stream) {
    s.video.srcObject = stream;
    playQuietly(s.video);
  }
}

function clearScheduleTimer(s: SnapshotSchedule): void {
  if (s.timerId !== null) {
    window.clearTimeout(s.timerId);
    s.timerId = null;
  }
}

function disposeSnapshotSchedule(s: SnapshotSchedule): void {
  clearScheduleTimer(s);
  s.unsubscribe();
  s.holders = 0;
  s.stopped = true; // ends an in-flight drain
  s.queue = [];
  bindScheduleStream(s, null);
  s.video = null;
  s.canvas = null;
  if (snapshotSchedule === s) snapshotSchedule = null;
}

const captureJpeg = (s: SnapshotSchedule): Promise<Blob | null> =>
  new Promise((resolve) => {
    const video = s.video;
    if (!video) {
      resolve(null);
      return;
    }
    const vw = video.videoWidth || 0;
    const vh = video.videoHeight || 0;
    const w = vw > 0 ? Math.min(SNAPSHOT_MAX_WIDTH, vw) : SNAPSHOT_MAX_WIDTH;
    const h =
      vw > 0 && vh > 0 ? Math.max(1, Math.round((vh * w) / vw)) : SNAPSHOT_FALLBACK_HEIGHT;
    if (!s.canvas) s.canvas = document.createElement("canvas");
    const canvas = s.canvas;
    if (canvas.width !== w || canvas.height !== h) {
      canvas.width = w;
      canvas.height = h;
    }
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      resolve(null);
      return;
    }
    try {
      ctx.drawImage(video, 0, 0, w, h);
      canvas.toBlob((blob) => resolve(blob), "image/jpeg", SNAPSHOT_QUALITY);
    } catch {
      resolve(null);
    }
  });

async function safeUpload(s: SnapshotSchedule, job: SnapshotJob): Promise<SnapshotUploadResult> {
  try {
    return await s.upload(job.blob, job.trigger, job.capturedAt, job.seq);
  } catch {
    return "retry";
  }
}

// Runs on whether or not anyone holds the schedule: a photo taken just before
// a Next must still arrive.
async function drainSnapshots(s: SnapshotSchedule): Promise<void> {
  if (s.inFlight) return;
  s.inFlight = true;
  try {
    while (s.queue.length > 0 && !s.stopped) {
      const job = s.queue.shift() as SnapshotJob;
      let result = await safeUpload(s, job);
      let tries = 1;
      for (const wait of SNAPSHOT_RETRY_BACKOFF_MS) {
        if (result !== "retry") break;
        await new Promise<void>((r) => window.setTimeout(r, wait));
        if (s.stopped) break;
        result = await safeUpload(s, job);
        tries += 1;
      }
      if (result === "retry" && !s.stopped) {
        // Dropped after the whole ladder. The server has no event type for
        // this (the ingest union is closed), so the gallery cannot show the
        // gap yet — Sentry hears it, with closed vocabulary only.
        captureSilentFailure("snapshot_dropped", { trigger: job.trigger, tries });
      }
      if (result === "stop") {
        s.stopped = true;
        s.queue = [];
        clearScheduleTimer(s);
        break;
      }
    }
  } finally {
    s.inFlight = false;
  }
}

type SnapshotAttempt = "taken" | "not_ready" | "skipped";

async function maybeSnapshot(
  s: SnapshotSchedule,
  trigger: SnapshotTrigger,
): Promise<SnapshotAttempt> {
  if (s.holders === 0 || s.stopped) return "skipped";
  const cfg = s.getConfig();
  if (!cfg) return "skipped";
  if (trigger !== "interval" && !cfg.extras.includes(trigger)) return "skipped";
  const video = s.video;
  if (!video || !video.srcObject || video.readyState < 2 /* HAVE_CURRENT_DATA */) {
    // Coax playback: Safari can reject the initial autoplay even muted +
    // playsInline, and a later play() after a user interaction often works.
    if (video?.srcObject && video.paused) playQuietly(video);
    return "not_ready";
  }
  const now = Date.now();
  if (now - s.lastShotAt < Math.max(1, cfg.min_gap_s) * 1000) return "skipped";
  s.lastShotAt = now;
  persistSchedulePlace(s);
  const blob = await captureJpeg(s);
  if (!blob || s.stopped) return "skipped";
  s.seq += 1;
  if (s.queue.length >= SNAPSHOT_QUEUE_CAP) s.queue.shift();
  s.queue.push({
    blob,
    trigger,
    capturedAt: new Date(now).toISOString(),
    seq: s.seq,
  });
  void drainSnapshots(s);
  return "taken";
}

// (Re)arms the one timer from the schedule's place: the next interval photo
// fires when it is due — at once when it already was — then every interval.
function armSnapshotSchedule(s: SnapshotSchedule): void {
  clearScheduleTimer(s);
  if (s.holders === 0 || s.stopped) return;
  const cfg = s.getConfig();
  if (!cfg) {
    s.timerId = window.setTimeout(() => {
      s.timerId = null;
      armSnapshotSchedule(s);
    }, SNAPSHOT_CONFIG_POLL_MS);
    return;
  }
  const intervalMs = Math.max(5, cfg.interval_s) * 1000;
  const due = Math.max(0, s.nextIntervalAt - Date.now());
  s.timerId = window.setTimeout(() => {
    s.timerId = null;
    // The next interval counts from the moment this one fired, not from the
    // end of the JPEG encode (seconds, when the face model is warming up the
    // GPU on the same thread) — so two photos are never less than an
    // interval apart, and never more by the encode.
    const firedAt = Date.now();
    void (async () => {
      const outcome = await maybeSnapshot(s, "interval");
      // Paused, stopped or re-armed (a hold) while the frame was encoding.
      if (s.holders === 0 || s.stopped || s.timerId !== null) return;
      if (outcome === "not_ready") {
        s.timerId = window.setTimeout(() => {
          s.timerId = null;
          armSnapshotSchedule(s);
        }, SNAPSHOT_NOT_READY_RETRY_MS);
        return;
      }
      // Taken, or skipped by the minimum gap after an extra: either way the
      // next interval photo is one interval on, as a plain interval timer
      // would have had it.
      s.nextIntervalAt = firedAt + intervalMs;
      persistSchedulePlace(s);
      armSnapshotSchedule(s);
    })();
  }, due);
}

/**
 * Hold the attempt's one snapshot schedule from a page on which the test is
 * RUNNING. Returns the release; the last release pauses the schedule in
 * place. The config is read lazily on every photo (null = no photos yet, or
 * ever); the latest holder's closures are the live ones.
 */
export function holdSnapshotSchedule(opts: SnapshotScheduleOptions): () => void {
  let s = snapshotSchedule;
  if (s && s.key !== opts.attemptId) {
    disposeSnapshotSchedule(s);
    s = null;
  }
  if (!s) {
    const place = readSchedulePlace(opts.attemptId);
    const created: SnapshotSchedule = {
      key: opts.attemptId,
      getConfig: opts.getConfig,
      upload: opts.upload,
      holders: 0,
      timerId: null,
      nextIntervalAt: place.next,
      lastShotAt: place.last,
      stopped: false,
      seq: 0,
      queue: [],
      inFlight: false,
      video: null,
      canvas: null,
      unsubscribe: () => {},
    };
    // Follows the singleton: a grant (the Gate's camera step after a pause),
    // a CP-14 re-acquire, a revoke or a dispose. A stream that (re)appears
    // makes a due photo possible again, so re-arm rather than wait a retry.
    created.unsubscribe = subscribeToStream((stream) => {
      bindScheduleStream(created, stream);
      if (stream && created.holders > 0) armSnapshotSchedule(created);
    });
    bindScheduleStream(created, getActiveStream());
    snapshotSchedule = created;
    s = created;
  } else {
    s.getConfig = opts.getConfig;
    s.upload = opts.upload;
  }
  const held = s;
  held.holders += 1;
  if (held.holders === 1) armSnapshotSchedule(held);
  let released = false;
  return () => {
    if (released) return;
    released = true;
    if (snapshotSchedule !== held) return; // a later attempt replaced it
    held.holders = Math.max(0, held.holders - 1);
    if (held.holders === 0) clearScheduleTimer(held); // paused — keeps its place
  };
}

/** An extra photo for an event (the frozen `extras`, the minimum gap). */
export function signalSnapshot(trigger: SnapshotTrigger): void {
  if (snapshotSchedule) void maybeSnapshot(snapshotSchedule, trigger);
}

export interface CameraMonitorOptions {
  emit: (payload: AntiCheatPayload) => void;
  // CP-14: durable emitter for the one event that must survive the monitor
  // being stopped mid-flight — the camera_started of a re-acquired stream.
  // `emit` is the controller's batch queue, a no-op once the controller is
  // disposed (every Question remount disposes it), so a re-acquire resolving
  // during a Next/Back would silently lose the label the server needs. Falls
  // back to `emit` when absent.
  emitDurable?: (payload: AntiCheatPayload) => void;
  intervalMs?: number;
  // EF-8: while started, the monitor HOLDS the attempt's one snapshot
  // schedule (see `holdSnapshotSchedule` — module-level, so a monitor
  // remounted by a Next/Back never restarts it). `getConfig` is read LAZILY
  // on every photo — the schedule arrives with the first /question payload,
  // after the monitor started. null = no photos (yet, or ever).
  snapshots?: {
    attemptId?: string;
    getConfig: () => SnapshotConfig | null;
    upload: SnapshotUploader;
  };
}

export interface CameraMonitor {
  start(stream: MediaStream): Promise<void>;
  stop(): void;
  // The page is being hidden (tab close, navigation away, the bfcache): emit
  // any open no-face stretch NOW, so it rides the controller's final beacon —
  // on a real unload stop() never runs. Called from the controller's
  // `onPageHide` (useAntiCheat), never from a pagehide listener of the
  // monitor's own, which would fire after that beacon. The monitor keeps
  // running: a page restored from the bfcache goes on watching, and the
  // stretch it already sent is not reported again.
  pageHide(): void;
  // EF-8: an extra photo for an event the monitor cannot see itself (fed by
  // the controller's onEvent: a fullscreen exit, a tab return).
  signal(trigger: "fullscreen_exit" | "tab_return"): void;
}

export function createCameraMonitor(opts: CameraMonitorOptions): CameraMonitor {
  const intervalMs = opts.intervalMs ?? DEFAULT_INTERVAL_MS;
  const emitDurable = opts.emitDurable ?? opts.emit;

  let video: HTMLVideoElement | null = null;
  let canvas: HTMLCanvasElement | null = null;
  let intervalId: number | null = null;
  let stopped = false;
  // EF-8: the monitor's hold on the attempt's snapshot schedule (start →
  // stop). The photos themselves live in the module-level schedule above.
  let releaseSchedule: (() => void) | null = null;
  let stream: MediaStream | null = null;
  // CP-14: true while a replacement stream is being requested after the track
  // ended. tick() must not read frames meanwhile — Chrome freezes the LAST
  // frame of an ended track and keeps readyState >= 2, so the detector would
  // keep "seeing" the candidate for as long as the re-acquire takes.
  let reacquiring = false;
  // Bumped by every track-ended handler and by stop(): a handler whose
  // generation went stale across its await was superseded (a second `ended`,
  // or a stop() that already nulled `video`/`stream`) and must not touch
  // monitor state.
  let reacquireGen = 0;

  let lastFaceCount: number | null = null;
  // Consecutive ticks where video.readyState < HAVE_CURRENT_DATA. Resets to 0
  // on the first tick with a decoded frame. After STALLED_TICK_GRACE ticks the
  // tick falls through with count=0 instead of silently returning, so the
  // recruiter still sees something accumulating when the off-DOM <video> never
  // starts (Safari autoplay rejection, slow camera init).
  let stalledTicks = 0;
  // Wall-clock time at which we first saw zero faces in the current stretch.
  // null when at least one face is visible.
  let noFaceStartedAt: number | null = null;
  // Two-tick consensus before committing a no-face stretch. The first missed
  // tick is treated as PENDING — could be the warmup race on Question.tsx
  // mount (first decoded frame hasn't propagated to the off-DOM <video> yet),
  // a lighting glitch, the candidate briefly looking at the keyboard, or a
  // detector flake. On the second consecutive miss we backdate
  // noFaceStartedAt to the first miss so the recruiter sees the accurate
  // stretch duration. Single-tick misses (very common in practice) emit no
  // event. Issue #10.
  let pendingMissAt: number | null = null;

  const cleanupVideo = (): void => {
    if (video) {
      video.pause();
      video.srcObject = null;
      video = null;
    }
    canvas = null;
  };

  const onTrackEnded = (): void => {
    void handleTrackEnded();
  };

  const attachStreamListeners = (s: MediaStream): void => {
    for (const track of s.getVideoTracks()) {
      track.addEventListener("ended", onTrackEnded);
    }
  };

  const detachStreamListeners = (): void => {
    if (!stream) return;
    for (const track of stream.getVideoTracks()) {
      track.removeEventListener("ended", onTrackEnded);
    }
  };

  // Emit any open no-face stretch and clear the consensus state. Called from
  // every teardown path (stop(), track-ended, pageHide()) so a pending-but-
  // unconfirmed miss (one tick into an absence, before the two-tick consensus
  // commits) is never silently dropped — a lifecycle exit mid-stretch is
  // itself proof the candidate is absent. Clearing is what keeps a page
  // restored from the bfcache from reporting the same stretch twice. Per
  // memory project_consensus_state_lifecycle_flush.
  // `noFaceStartedAt` and `pendingMissAt` are mutually exclusive (committing
  // one nulls the other), so `??` picks whichever is open.
  const flushNoFaceStretch = (): void => {
    const openSince = noFaceStartedAt ?? pendingMissAt;
    if (openSince === null) return;
    opts.emit({
      type: "camera_no_face",
      stretch_ms: Math.max(0, Date.now() - openSince),
    });
    noFaceStartedAt = null;
    pendingMissAt = null;
  };

  // Hoisted (not only a method on the returned literal) so the track-ended
  // handler can call it without depending on `this`.
  const stop = (): void => {
    if (stopped) return;
    stopped = true;
    reacquireGen++;
    if (intervalId !== null) {
      window.clearInterval(intervalId);
      intervalId = null;
    }
    // The schedule pauses in place (or carries on under the next holder); a
    // photo already queued keeps uploading.
    releaseSchedule?.();
    releaseSchedule = null;
    // Emit any trailing no-face stretch so it survives unmount.
    flushNoFaceStretch();
    detachStreamListeners();
    cleanupVideo();
    stream = null;
  };

  // The pre-CP-14 track-ended path, unchanged: surface the loss as a revoke,
  // flush any open no-face stretch (the camera dying mid-stretch is itself
  // proof the candidate is absent), tear the singleton down so subscribers
  // (CameraPreview) unmount synchronously, and stop.
  const giveUp = (): void => {
    opts.emit({ type: "camera_permission_denied", reason: "revoked" });
    flushNoFaceStretch();
    disposeActiveStream();
    stop();
  };

  const bindStream = (s: MediaStream): void => {
    stream = s;
    if (video) video.srcObject = s;
    attachStreamListeners(s);
  };

  // CP-14. Track 'ended' fires on permission revocation (Chrome's omnibox
  // toggle, site settings) AND when the device goes away (unplugged,
  // disabled, a virtual camera swapped in). Try to re-acquire: in the second
  // case permission is still granted, so getUserMedia resolves silently on
  // whatever camera the browser now prefers, monitoring continues, and the
  // new device's label — reported via camera_started — is what lets the
  // server derive "camera switched". When the permission really was revoked,
  // getUserMedia rejects at once and the old revoke path runs unchanged.
  const handleTrackEnded = async (): Promise<void> => {
    if (stopped) return;
    const gen = ++reacquireGen;
    detachStreamListeners();
    if (autoReacquireExhausted || autoReacquires >= MAX_AUTO_REACQUIRES) {
      autoReacquireExhausted = true;
      giveUp();
      return;
    }
    autoReacquires++;
    reacquiring = true;
    let next: MediaStream | null = null;
    try {
      next = await withTimeout(requestCameraStream(), REACQUIRE_TIMEOUT_MS);
    } catch {
      next = null;
      autoReacquireExhausted = true;
    }
    if (stopped) {
      // Stopped (route change) while waiting. A stream that arrived already
      // sits in the singleton for the next Question mount — which can't know
      // it is new — so report its label from here, on the durable path.
      if (next) {
        emitDurable({
          type: "camera_started",
          device_label: describeCameraDevice(next),
        });
      }
      return;
    }
    if (gen !== reacquireGen) return; // superseded by a newer `ended`
    reacquiring = false;
    if (!next) {
      giveUp();
      return;
    }
    bindStream(next);
    emitDurable({
      type: "camera_started",
      device_label: describeCameraDevice(next),
    });
  };

  const tick = async (): Promise<void> => {
    if (stopped || reacquiring || !video || !canvas) return;
    // EF-8: no detector ⇒ no presence claims, but the photos (the schedule
    // held in start()) keep running. Returning here (rather than falling
    // through with count = 0)
    // is the point: the stalled-video path below would otherwise accumulate
    // "no face" stretches nobody measured, blaming the candidate for their
    // browser. A model that finishes loading later resumes detection on the
    // next tick, because this re-reads the module-level flag.
    if (!modelsReady || !faceapiMod) return;
    let count = 0;
    if (video.readyState < 2 /* HAVE_CURRENT_DATA */) {
      // Try to coax playback. Safari can reject the initial autoplay even
      // with muted+playsInline; the rejection is swallowed in start(), but a
      // later play() after the user has interacted with the page often
      // succeeds. Fire-and-forget — we'll check readyState again next tick.
      if (video.paused) {
        video.play().catch(() => {});
      }
      stalledTicks++;
      if (stalledTicks <= STALLED_TICK_GRACE) return;
      // Persistent stall: fall through with count = 0 so the state machine
      // accumulates a synthetic miss instead of silently noop'ing forever.
    } else {
      stalledTicks = 0;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      try {
        const detections = await faceapiMod.detectAllFaces(
          canvas,
          new faceapiMod.TinyFaceDetectorOptions({
            inputSize: DETECTOR_INPUT_SIZE,
            scoreThreshold: DETECTOR_SCORE_THRESHOLD,
          }),
        );
        count = detections.length;
      } catch {
        // Treat a detector error as "no signal this tick" — don't spam events
        // and don't crash the monitor.
        return;
      }
    }
    if (stopped) return;
    const now = Date.now();
    if (count === 0) {
      if (noFaceStartedAt === null) {
        if (pendingMissAt === null) {
          pendingMissAt = now;
        } else {
          // Second consecutive miss — commit. Backdate to the first miss
          // so stretch_ms stays accurate (counts from when we first noticed).
          noFaceStartedAt = pendingMissAt;
          pendingMissAt = null;
        }
      }
    } else {
      // Any confirmed face cancels the pending state — single-tick blips
      // are suppressed by design.
      pendingMissAt = null;
      if (noFaceStartedAt !== null) {
        opts.emit({
          type: "camera_no_face",
          stretch_ms: Math.max(0, now - noFaceStartedAt),
        });
        noFaceStartedAt = null;
        signalSnapshot("face_return");
      }
      if (count >= 2 && lastFaceCount !== count) {
        opts.emit({ type: "camera_multi_face", face_count: count });
        signalSnapshot("multi_face");
      }
    }
    lastFaceCount = count;
  };

  return {
    async start(s: MediaStream): Promise<void> {
      if (intervalId !== null) return;
      stream = s;
      // EF-8: hold the attempt's one snapshot schedule BEFORE any await, so
      // a Next/Back (stop → start within one React commit) releases and
      // re-holds it synchronously — the schedule keeps its place, and no
      // photo is taken for the route change unless one was due.
      if (opts.snapshots) {
        releaseSchedule = holdSnapshotSchedule({
          attemptId: opts.snapshots.attemptId ?? "",
          getConfig: opts.snapshots.getConfig,
          upload: opts.snapshots.upload,
        });
      }
      // The detector is OPTIONAL, and nothing below waits for it. The
      // <video>, the canvases, the track listeners and the snapshot schedule
      // need the stream and nothing else, so the model load runs ALONGSIDE
      // them — never in front. start() used to `await loadModels()`, which
      // threw out of start() before any of this ran, and the attempt then
      // recorded no photos, no presence AND no revocation, with nothing saying
      // why (OPS-18 rehearsal, 2026-09-18). Awaiting it with a timeout instead
      // (the first fix) still lost the first 20 s of photos and — worse — the
      // whole revocation window, because the listeners below had not been
      // attached yet when the track ended: an 'ended' that fires before
      // attachStreamListeners is gone for good. So: set up, then report.
      const modelLoad = withTimeout(loadModels(), MODEL_LOAD_TIMEOUT_MS);

      video = document.createElement("video");
      video.autoplay = true;
      video.muted = true;
      video.playsInline = true;
      video.srcObject = s;
      // Off-DOM is fine — drawing to the canvas only needs frames decoded.
      try {
        await video.play();
      } catch {
        // Some browsers reject autoplay even when muted+srcObject; the
        // interval will still tick and call drawImage once readyState advances.
      }

      canvas = document.createElement("canvas");
      canvas.width = CAPTURE_WIDTH;
      canvas.height = CAPTURE_HEIGHT;

      // Track 'ended' — permission revoked (Chrome's omnibox toggle, browser
      // settings) or the device gone (unplugged / swapped). See
      // handleTrackEnded: a re-acquire is attempted before giving up.
      attachStreamListeners(s);

      // First tick fires immediately so a denied-mid-test or zero-face state
      // is detected without a 10 s delay. Subsequent ticks at intervalMs.
      // Whether that first tick can DETECT depends on the weights already
      // being there — Gate.tsx warms them on idle, so normally they are. When
      // they are not, a catch-up tick below does the detection the moment
      // they land (the photos never waited on them: see the hold above).
      const detectorWasReady = modelsReady;
      void tick();
      intervalId = window.setInterval(() => {
        void tick();
      }, intervalMs);

      // The detector's verdict, once it has one. A load that rejects (no tfjs
      // backend, a blocked chunk) or never lands inside MODEL_LOAD_TIMEOUT_MS
      // is reported ONCE, durably: it says why presence is silent for the
      // whole attempt, so it must survive the controller being disposed by a
      // remount — the same reason camera_started uses this path (CP-14). A
      // timed-out load that lands later still resumes detection on the next
      // tick (tick() re-reads the module-level `modelsReady`); the note stays
      // as the record of the gap.
      void modelLoad.then(
        () => {
          // The immediate tick above could not detect: do it now rather than
          // lose a whole interval to a cold (or merely slow) model.
          if (!stopped && !detectorWasReady) void tick();
        },
        () => {
          if (stopped) return;
          emitDurable({
            type: "camera_check_unavailable",
            reason: "models_unavailable",
          });
        },
      );
    },
    stop,
    pageHide: flushNoFaceStretch,
    signal(trigger: "fullscreen_exit" | "tab_return"): void {
      signalSnapshot(trigger);
    },
  };
}
